// The single coordinator between the synced collections and a backend. It owns
// the outbox, the backend reference, and the per-user pull cursor, and runs both
// directions of sync: PUSH (drain the outbox up) and, since phase 3, PULL+MERGE
// (bring the account's docs down and reconcile, last-write-wins).
//
// Lifecycle: a backend is absent until a user signs in (auth calls `connect`).
// While absent, `enabled` is false and the synced stores stay pure local
// passthroughs — no queueing, no behavior change, no outbox growth. On connect
// the controller, in order: optionally CLAIMS pre-existing local docs (first
// login on this device), PULLS+MERGES the account's docs, then FLUSHES queued
// writes up. A failed push/pull leaves work queued for the next attempt.

import type { IDBStore } from "../model/idb-store";
import type { Outbox, OutboxEntry, OutboxOpType } from "./outbox";
import { createOutbox } from "./outbox";
import type { SyncBackend, SyncRecord } from "./backend";
import { type HighWaterStore, createHighWaterStore } from "./high-water";
import { ANONYMOUS_SCOPE, storeForScope } from "./account-scope";
import { MAX_DOC_BYTES, docTooLargeMessage, isPermanentSyncError, syncedRowBytes } from "./limits";

// The shape every synced record shares; the controller never inspects the
// payload, only its id + LWW timestamp.
type Syncable = { id: string; updatedAt: number };

// Merge order: a layout's placements reference models by id, so models must be
// applied to local storage before the layouts that point at them. Tables not
// listed merge last (order among them doesn't matter).
const MERGE_ORDER = ["models", "layouts"];
function mergeRank(table: string): number {
  const i = MERGE_ORDER.indexOf(table);
  return i === -1 ? MERGE_ORDER.length : i;
}

// Asked once, on first sign-in for a device, when local docs exist: "add these
// to your account?" Returns true to claim (push them up). Injected by the UI so
// the controller stays free of DOM/modal deps; absent → claim by default.
export type ClaimDecider = (counts: Record<string, number>) => Promise<boolean>;

// Surfaced to the UI so the user can see sync is happening / failed.
//   idle    — no backend (signed out)
//   syncing — a push/pull is in flight
//   synced  — connected, nothing pending, last op succeeded
//   error   — the last push/pull failed (will retry); `error` carries the reason
export type SyncStatus = "idle" | "syncing" | "synced" | "error";
export type StatusListener = (status: SyncStatus, error: string | null) => void;

export class SyncController {
  private backend: SyncBackend | null = null;
  // The account scope of the current connection. Pull/claim/flush address local
  // storage through THIS scope (not whatever scope is active at call time), so a
  // sign-out racing an in-flight operation can't write into the wrong pool.
  // Plain (unscoped) stores — the tests' in-memory fakes — ignore it.
  private scopeId: string = ANONYMOUS_SCOPE;
  private readonly locals = new Map<string, IDBStore<Syncable>>();
  // Per-table fields that live only on-device (e.g. a model's reference photo) and
  // are absent from pulled payloads — carried over from the local copy on merge
  // so a pull doesn't wipe them.
  private readonly localOnly = new Map<string, string[]>();
  // Flushes are serialized through one promise chain so concurrent triggers
  // can't interleave (and so a caller can await a full drain deterministically).
  private chain: Promise<void> = Promise.resolve();
  private claimDecider: ClaimDecider | null = null;
  private readonly changeListeners = new Set<() => void>();
  // Sync status, derived from in-flight ops + last error and pushed to the UI.
  private active = 0;
  private lastError: string | null = null;
  private status: SyncStatus = "idle";
  private readonly statusListeners = new Set<StatusListener>();
  // Records whose push failed PERMANENTLY (size cap / constraint violation):
  // their outbox entries were dropped so they don't retry-loop, the local copy
  // is untouched, and the message stays surfaced (status "error") until the
  // record is re-saved (a fresh enqueue clears its failure and tries again) or
  // the session ends. Keyed like the outbox: `${table}:${recordId}`.
  private readonly permanentFailures = new Map<string, string>();
  // The error text last delivered to listeners, so a CHANGED message refires
  // even when the coarse status stays "error".
  private notifiedError: string | null = null;

  constructor(
    private readonly outbox: Outbox = createOutbox(),
    private readonly highWater: HighWaterStore = createHighWaterStore(),
  ) {}

  // True once a backend is attached — the gate the synced stores check before
  // queueing a write.
  get enabled(): boolean {
    return this.backend !== null;
  }

  // A synced store registers its live (raw, un-wrapped) local store. flush reads
  // it to build push payloads; merge writes pulled records straight to it —
  // bypassing the synced wrapper so a pulled record doesn't re-enter the outbox.
  // `localOnlyFields` are preserved from the local copy when a pull overwrites it.
  register(table: string, local: IDBStore<Syncable>, opts?: { localOnlyFields?: string[] }): void {
    this.locals.set(table, local);
    if (opts?.localOnlyFields?.length) this.localOnly.set(table, opts.localOnlyFields);
  }

  // The UI registers how to ask the user about claiming local docs.
  setClaimDecider(decider: ClaimDecider): void {
    this.claimDecider = decider;
  }

  // Fired after a pull merges changes into local storage, so the UI (collection
  // dock) can re-list. Returns an unsubscribe.
  onChange(cb: () => void): () => void {
    this.changeListeners.add(cb);
    return () => this.changeListeners.delete(cb);
  }

  // Subscribe to sync-status changes (for the account panel's indicator). Fires
  // the current status immediately. Returns an unsubscribe.
  onStatus(cb: StatusListener): () => void {
    this.statusListeners.add(cb);
    cb(this.status, this.notifiedError);
    return () => this.statusListeners.delete(cb);
  }

  // Transient errors (lastError, cleared by the next success) take precedence
  // for display; otherwise a sticky permanent failure keeps status "error".
  private currentError(): string | null {
    if (this.lastError) return this.lastError;
    for (const msg of this.permanentFailures.values()) return msg;
    return null;
  }

  private recomputeStatus(): void {
    const err = this.currentError();
    const next: SyncStatus =
      this.active > 0 ? "syncing" : err ? "error" : this.enabled ? "synced" : "idle";
    if (next === this.status && err === this.notifiedError) return;
    this.status = next;
    this.notifiedError = err;
    for (const cb of this.statusListeners) cb(next, err);
  }

  // Run a network op while reflecting it in the status. Clears the error on
  // success; records + LOGS it on failure (no longer swallowed silently) and
  // re-throws so callers keep their retry semantics.
  private async tracked<T>(label: string, fn: () => Promise<T>): Promise<T> {
    this.active++;
    this.recomputeStatus();
    try {
      const out = await fn();
      this.lastError = null;
      return out;
    } catch (e) {
      this.lastError = (e as Error)?.message ?? String(e);
      console.warn(`[sync] ${label} failed:`, this.lastError);
      throw e;
    } finally {
      this.active--;
      this.recomputeStatus();
    }
  }

  // Attach a backend (on sign-in) and run the full connect sequence. `userId`
  // keys the per-account pull cursor (and "first login on this device" check).
  // Returns the sequence promise; callers (the app) may ignore it to avoid
  // blocking the UI on sync, while tests can await it.
  connect(backend: SyncBackend, userId = "local"): Promise<void> {
    this.backend = backend;
    this.scopeId = userId;
    this.lastError = null;
    this.permanentFailures.clear(); // fresh session, fresh verdicts
    this.recomputeStatus();
    return this.onConnect(userId);
  }

  // Detach (on sign-out). Queued entries + pull cursors persist for next time;
  // permanent failures don't (they belong to the ended session's account).
  disconnect(): void {
    this.backend = null;
    this.lastError = null;
    this.permanentFailures.clear();
    this.recomputeStatus();
  }

  // Claim (first login here) → pull+merge → flush. Failures are swallowed so a
  // network/auth hiccup just defers work to the next connect; nothing is lost.
  private async onConnect(userId: string): Promise<void> {
    try {
      if (!this.hasSyncedBefore(userId)) {
        const counts = await this.localCounts();
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        if (total > 0) {
          const claim = this.claimDecider ? await this.claimDecider(counts) : true;
          if (claim) await this.claimLocal();
        }
      }
      await this.pullMerge(userId);
    } catch {
      // leave queued writes + cursor as-is; the next connect/flush retries
    }
    void this.flush();
  }

  // Whether this account has ever pulled on this device (any table cursor set).
  private hasSyncedBefore(userId: string): boolean {
    for (const table of this.locals.keys()) {
      if (this.highWater.has(this.hwKey(userId, table))) return true;
    }
    return false;
  }

  private hwKey(userId: string, table: string): string {
    // "v2": per-account store scoping changed where pulled records land, so
    // cursors written under the old (shared-pool) scheme must not suppress the
    // full pull that populates an account's fresh scoped database.
    return `tinydraft.sync.v2.pulledAt.${userId}.${table}`;
  }

  // Count the CLAIMABLE records per collection (for the claim prompt copy):
  // the anonymous pool — the docs made on this device before signing in. The
  // account's own scoped pool is never "claimed", it already belongs to it.
  async localCounts(): Promise<Record<string, number>> {
    const out: Record<string, number> = {};
    for (const [table, local] of this.locals) {
      out[table] = (await storeForScope(local, ANONYMOUS_SCOPE).listKeys()).length;
    }
    return out;
  }

  // The "yes, claim my local docs" path: MOVE each anonymous-pool record into
  // the connected account's pool and enqueue it for upload. flush (called by
  // onConnect after) pushes them; the server's lww_guard keeps anything newer
  // that's already up there. Moving (not copying) is what keeps a claimed doc
  // out of the anonymous pool — and out of the next account's claim prompt.
  // With unscoped stores (tests) source and destination coincide, so this
  // degrades to the old enqueue-everything behavior.
  async claimLocal(): Promise<number> {
    let n = 0;
    for (const [table, local] of this.locals) {
      const from = storeForScope(local, ANONYMOUS_SCOPE);
      const to = storeForScope(local, this.scopeId);
      for (const item of await from.list()) {
        // `to` is the raw (un-synced) store — this put does not re-enter the
        // outbox; the explicit enqueue below records the upload intent.
        if (to !== from) await to.put(item);
        await this.outbox.put({
          id: `${table}:${item.id}`,
          table,
          recordId: item.id,
          type: "put",
          updatedAt: item.updatedAt,
        });
        if (to !== from) await from.remove(item.id);
        n++;
      }
    }
    return n;
  }

  // Pull each collection's changes-since-cursor and merge them into local by
  // last-write-wins, applying collections in reference order (models first).
  // Advances the per-table cursor and notifies listeners if anything changed.
  // Returns the number of local records added/updated/removed.
  async pullMerge(userId: string): Promise<number> {
    const backend = this.backend;
    if (!backend) return 0;
    let applied = 0;

    const tables = [...this.locals.keys()].sort((a, b) => mergeRank(a) - mergeRank(b));
    for (const table of tables) {
      const registered = this.locals.get(table);
      if (!registered) continue;
      // Pin to the pulling account's pool: a sign-out (scope swap) racing this
      // merge must not divert the remaining writes into the anonymous pool.
      const local = storeForScope(registered, userId);
      const since = this.highWater.get(this.hwKey(userId, table));
      const records = await this.tracked(`pull ${table}`, () => backend.pull(table, since));

      let maxTs = since;
      for (const r of records) {
        // Disconnected mid-merge (sign-out): stop without advancing the cursor,
        // so the next connect re-pulls what this pass didn't finish applying.
        if (this.backend !== backend) return applied;
        if (r.updatedAt > maxTs) maxTs = r.updatedAt;
        const existing = await local.get(r.id);
        if (r.deleted) {
          // Tombstone wins only if it's at least as new as the local copy.
          if (existing && r.updatedAt >= existing.updatedAt) {
            await local.remove(r.id);
            applied++;
          }
        } else if (r.payload && (!existing || r.updatedAt > existing.updatedAt)) {
          await local.put(this.withLocalOnly(table, r.payload, existing));
          applied++;
        }
      }
      this.highWater.set(this.hwKey(userId, table), maxTs);
    }

    if (applied > 0) for (const cb of this.changeListeners) cb();
    return applied;
  }

  // Carry this table's local-only fields from the existing record onto a pulled
  // payload, so a merge doesn't drop device-local data (e.g. a reference photo).
  private withLocalOnly(table: string, payload: unknown, existing: Syncable | undefined): Syncable {
    const fields = this.localOnly.get(table);
    if (!fields || !existing) return payload as Syncable;
    const merged = { ...(payload as Record<string, unknown>) };
    const prev = existing as Record<string, unknown>;
    for (const f of fields) if (prev[f] !== undefined) merged[f] = prev[f];
    return merged as unknown as Syncable;
  }

  // Record a pending mutation durably, then kick the (fire-and-forget) flush.
  // The outbox write is awaited so the sync intent survives a reload even if the
  // push hasn't happened; the network push is not awaited here.
  async enqueue(
    table: string,
    type: OutboxOpType,
    recordId: string,
    updatedAt: number,
  ): Promise<void> {
    // A fresh save of a permanently-failed record clears its verdict — the
    // user may have shrunk it, so it gets one full push attempt again.
    if (this.permanentFailures.delete(`${table}:${recordId}`)) this.recomputeStatus();
    await this.outbox.put({ id: `${table}:${recordId}`, table, recordId, type, updatedAt });
    void this.flush();
  }

  // Drain the outbox into the backend. Serialized via the promise chain, so the
  // returned promise resolves once this drain (and any already queued) finishes
  // — concurrent callers never run two drains at once.
  flush(): Promise<void> {
    this.chain = this.chain.catch(() => {}).then(() => this.drain());
    return this.chain;
  }

  // One full drain pass: push each collection's queued entries, removing them as
  // they land. Loops until the outbox is empty so entries queued mid-push are
  // caught. A push failure aborts the pass and leaves the rest queued.
  private async drain(): Promise<void> {
    const backend = this.backend;
    if (!backend) return;
    const scope = this.scopeId; // payload reads pin to the connected account's pool
    for (;;) {
      // Disconnected since the pass started: stop. The outbox routes by active
      // scope, so continuing would read (and remove) the wrong account's queue.
      if (this.backend !== backend) return;
      const entries = await this.outbox.all();
      if (entries.length === 0) return;

      // Group by table; one push per collection.
      const byTable = new Map<string, typeof entries>();
      for (const e of entries) {
        const group = byTable.get(e.table);
        if (group) group.push(e);
        else byTable.set(e.table, [e]);
      }

      for (const [table, group] of byTable) {
        const registered = this.locals.get(table);
        const local = registered ? storeForScope(registered, scope) : undefined;
        const records: SyncRecord[] = [];
        const pushable: OutboxEntry[] = [];
        for (const e of group) {
          // Re-read the live record so the payload is never stale. A "put"
          // whose record has since vanished locally becomes a tombstone.
          const rec = e.type === "put" && local ? await local.get(e.recordId) : undefined;
          if (rec) {
            // Pre-push size gate (mirrors the server's row cap, see limits.ts):
            // an oversize document is a PERMANENT failure — pushing it would
            // bounce off the constraint on every retry, forever. Drop the
            // entry (local copy untouched), surface the message, keep going.
            if (syncedRowBytes(rec) > MAX_DOC_BYTES) {
              await this.failPermanently(e, docTooLargeMessage(rec));
              continue;
            }
            records.push({ id: e.recordId, updatedAt: rec.updatedAt, deleted: false, payload: rec });
          } else {
            records.push({ id: e.recordId, updatedAt: e.updatedAt, deleted: true, payload: null });
          }
          pushable.push(e);
        }
        if (pushable.length === 0) continue; // whole group was dropped as oversize

        try {
          // tracked() logs + records the error and re-throws.
          await this.tracked(`push ${table}`, () => backend.push(table, records));
        } catch (err) {
          // The server rejected the batch for a reason retrying can't fix
          // (constraint violation, malformed payload — see limits.ts): drop
          // these entries so they don't retry-loop; local copies stay intact.
          if (isPermanentSyncError(err)) {
            const msg = (err as Error).message;
            for (const e of pushable) await this.failPermanently(e, msg);
            continue;
          }
          return; // transient (network/auth) — leave queued; a later flush retries
        }
        for (const e of pushable) await this.outbox.remove(e.id);
      }
    }
  }

  // Retire an outbox entry whose push can never succeed: dequeue it (ending
  // the retry loop), remember the reason so the status UI shows it, and log.
  // The local IndexedDB copy is deliberately untouched — only sync gave up.
  private async failPermanently(e: OutboxEntry, message: string): Promise<void> {
    await this.outbox.remove(e.id);
    this.permanentFailures.set(e.id, message);
    console.warn(`[sync] permanent push failure for ${e.id}:`, message);
    this.recomputeStatus();
  }
}

// The process-wide controller every synced collection shares (one outbox, one
// flush loop, one backend). Tests construct their own with injected fakes.
export const syncController = new SyncController();
